import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, AlertTriangle, Send, FileText, Edit } from "lucide-react";

const SEVERITY = { low: { l: "Faible", c: "bg-blue-100" }, medium: { l: "Moyen", c: "bg-yellow-100" }, high: { l: "Élevé", c: "bg-orange-200" }, critical: { l: "CRITIQUE", c: "bg-red-200 font-bold" } };
const STATUS = { open: { l: "Ouvert", c: "bg-yellow-100" }, in_progress: { l: "En cours", c: "bg-blue-100" }, resolved: { l: "Résolu", c: "bg-emerald-100" }, closed: { l: "Clôturé", c: "bg-slate-200" } };

export default function IncidentsAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [opened, setOpened] = useState(null);
  const [comment, setComment] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const load = () => api.get("/admin/incidents").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const update = async () => {
    try {
      const payload = {};
      if (newStatus) payload.status = newStatus;
      if (comment) payload.admin_comment = comment;
      await api.put(`/admin/incidents/${opened.id}`, payload);
      toast.success("Mis à jour"); setOpened(null); setComment(""); setNewStatus("");
      load();
    } catch { toast.error("Échec"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar /><Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</button>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-2">Incidents & sinistres</h1>
        <p className="text-sm opacity-70 mb-8">Suivi des signalements des locataires.</p>
        <div className="bg-[color:var(--bg-surface)] border overflow-x-auto" style={{ borderColor: "var(--border-default)" }}>
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white"><tr>
              <th className="px-3 py-3 text-left text-xs uppercase">Date</th>
              <th className="px-3 py-3 text-left text-xs uppercase">Locataire</th>
              <th className="px-3 py-3 text-left text-xs uppercase">Sévérité</th>
              <th className="px-3 py-3 text-left text-xs uppercase">Type</th>
              <th className="px-3 py-3 text-left text-xs uppercase">Titre</th>
              <th className="px-3 py-3 text-left text-xs uppercase">Statut</th>
              <th className="px-3 py-3 text-right text-xs uppercase">Actions</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="7" className="p-12 text-center opacity-50">Aucun incident.</td></tr>}
              {items.map(i => (
                <tr key={i.id} className="border-t border-slate-900/10" data-testid={`row-incident-${i.id}`}>
                  <td className="px-3 py-3 text-xs font-mono">{new Date(i.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-3 py-3 text-xs"><div className="font-semibold">{i.tenant?.name}</div><div className="opacity-50">{i.tenant?.email}</div></td>
                  <td className="px-3 py-3"><span className={`text-xs font-bold uppercase px-2 py-1 ${SEVERITY[i.severity]?.c}`}>{SEVERITY[i.severity]?.l}</span></td>
                  <td className="px-3 py-3 text-xs">{i.type}</td>
                  <td className="px-3 py-3 text-xs font-semibold">{i.title}</td>
                  <td className="px-3 py-3"><span className={`text-xs font-bold uppercase px-2 py-1 ${STATUS[i.status]?.c}`}>{STATUS[i.status]?.l}</span></td>
                  <td className="px-3 py-3 text-right"><button onClick={() => { setOpened(i); setNewStatus(i.status); }} data-testid={`view-incident-${i.id}`} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white"><Edit className="w-4 h-4"/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {opened && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpened(null)}>
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-900" onClick={e => e.stopPropagation()} data-testid="incident-modal">
              <div className="p-6 border-b border-slate-900/10">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-[#FFC107]"/><h2 className="font-display font-black text-xl">{opened.title}</h2></div>
                <div className="flex gap-2 flex-wrap">
                  <span className={`text-xs font-bold uppercase px-2 py-1 ${SEVERITY[opened.severity]?.c}`}>{SEVERITY[opened.severity]?.l}</span>
                  <span className="text-xs font-mono px-2 py-1 bg-slate-100">{opened.type}</span>
                  <span className="text-xs font-mono px-2 py-1 bg-slate-100">{opened.tenant?.name}</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1">Description</div>
                  <p className="text-sm whitespace-pre-line bg-slate-50 p-3 border-l-4 border-[#FFC107]">{opened.description}</p>
                </div>
                {opened.attachments?.length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-2">Pièces jointes ({opened.attachments.length})</div>
                    <div className="grid grid-cols-3 gap-2">
                      {opened.attachments.map((a, idx) => (
                        <a key={idx} href={a.url.startsWith("http") ? a.url : `${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="border border-slate-900/15 p-2 text-xs flex items-center gap-1 hover:bg-[#FFC107]"><FileText className="w-3 h-3"/>{a.name}</a>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-2">Historique</div>
                  <div className="space-y-2">
                    {opened.history?.map((h, idx) => (
                      <div key={idx} className="border-l-2 border-slate-200 pl-3 py-1 text-xs">
                        <div className="font-bold">{h.by_name} · {new Date(h.at).toLocaleString("fr-FR")}</div>
                        <div className="opacity-70">{h.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <F label="Statut">
                  <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="input" data-testid="incident-status">
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                  </select>
                </F>
                <F label="Commentaire interne (ajouté à l'historique)">
                  <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)} className="input resize-none" data-testid="incident-comment" placeholder="Commentaire pour l'historique..." />
                </F>
              </div>
              <div className="p-6 border-t border-slate-900/10 flex justify-end gap-3">
                <button onClick={() => setOpened(null)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase">Fermer</button>
                <button onClick={update} data-testid="incident-save" className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase">Enregistrer</button>
              </div>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
